// TabApproval.jsx
import { useEffect, useState } from "react";
import {
  Card,
  Typography,
  Input,
  Button,
  message,
  Spin,
  Divider,
  Checkbox,
  Row,
  Modal,
  Alert,
} from "antd";
import axios from "axios";
import { useParams } from "react-router-dom";
import useRoleAccess from "@/hooks/useRoleAccess";

const { TextArea } = Input;
const { Title, Text } = Typography;

const approverMap = {
  // approver_1: "indra_hartanto",
  approver_1: ["indra_hartanto", "deo", "g_prastiwi", "david_h"],
  approver_2: "dian_andrianza",
  approver_3: "sastra_hamidjaja",
};

const TabApproval = () => {
  const { application_id, nik } = useParams();
  const { isViewer } = useRoleAccess();
  const [approvalNotes, setApprovalNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentApprover, setCurrentApprover] = useState("");
  const [isFinal, setIsFinal] = useState(false);
  const [isReject, setIsReject] = useState(false);
  const [loading, setLoading] = useState(true);
  const [finalApproved, setFinalApproved] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);

  const API_BASE_URL2 = import.meta.env.VITE_BASE_URL2;

  // === Tambahan: username dari localStorage ===
  const loginUser = localStorage.getItem("username");

  useEffect(() => {
    if (application_id && nik) {
      fetchApprovalNotes();
    }
  }, [application_id, nik]);

  const fetchApprovalNotes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL2}/api/approval/${application_id}/${nik}`
      );
      const data = res.data;
      const notes = Array.isArray(data) ? data : data ? [data] : [];
      setApprovalNotes(notes);

      const anyFinal = notes.some((note) => note.is_final_approve);
      const anyRejected = notes.some((note) => note.is_final_reject);

      setFinalApproved(anyFinal);
      setRejected(anyRejected);

      if (!anyFinal && !anyRejected) {
        const approverCount = notes.length;
        if (approverCount === 0) setCurrentApprover("approver_1");
        else if (approverCount === 1) setCurrentApprover("approver_2");
        else if (approverCount === 2) setCurrentApprover("approver_3");
      }
    } catch (err) {
      console.error("Failed to load approval notes", err);
      setApprovalNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!newNote.trim()) return;

    if (isReject) {
      setRejectModalVisible(true);
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL2}/api/approval`, {
        application_id,
        nik,
        approved_by: currentApprover,
        approval_note: newNote,
        is_final_approve: isFinal,
        is_final_reject: false,
        // created_by: approverMap[currentApprover],
        created_by: loginUser,
      });

      let newStatus = "CRA";
      if (isFinal) {
        newStatus = "FA";
      } else if (currentApprover === "approver_1") {
        newStatus = "APPV-2";
      } else if (currentApprover === "approver_2") {
        newStatus = "APPV-3";
      } else if (currentApprover === "approver_3") {
        newStatus = "FA";
      }

      await axios.put(
        `${API_BASE_URL2}/api/updatestatus/${application_id}/${nik}`,
        { status: newStatus }
      );

      message.success("Catatan approval berhasil disimpan.");
      setNewNote("");
      setIsFinal(false);
      setIsReject(false);
      await fetchApprovalNotes();
    } catch (err) {
      console.error(err);
      message.error("Gagal menyimpan catatan approval.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL2}/api/approval`, {
        application_id,
        nik,
        approved_by: currentApprover,
        approval_note: newNote,
        is_final_approve: false,
        is_final_reject: true,
        // created_by: approverMap[currentApprover],
        created_by: loginUser,
      });

      await axios.put(
        `${API_BASE_URL2}/api/updatestatus/${application_id}/${nik}`,
        { status: "REJECTED" }
      );

      message.success("Pengajuan berhasil direject.");
      setNewNote("");
      setIsFinal(false);
      setIsReject(false);
      await fetchApprovalNotes();
    } catch (err) {
      console.error(err);
      message.error("Gagal melakukan reject.");
    } finally {
      setIsSubmitting(false);
      setRejectModalVisible(false);
    }
  };

  if (loading) return <Spin size="large" />;

  return (
    <div className="space-y-6">
      <Title level={4}>Catatan Approval</Title>

      {rejected && (
        <Alert
          message="Pengajuan ini telah di REJECT ❌"
          type="error"
          showIcon
          className="mb-4"
        />
      )}

      {approvalNotes.length > 0 && (
        <div className="space-y-4">
          {approvalNotes.map((note, index) => (
            <Card
              key={index}
              title={`Catatan ${note.approved_by}`}
              className="border border-gray-300"
            >
              <Text className="block mb-1 text-gray-600">
                Input by: <strong>{note.created_by}</strong>
              </Text>
              <Text className="block mb-2 text-gray-500 text-sm">
                {note.created_date
                  ? new Date(note.created_date).toLocaleString()
                  : "-"}
              </Text>
              <Text className="whitespace-pre-wrap text-gray-800">
                {note.approval_note}
              </Text>
              {note.is_final_approve && (
                <Text
                  type="success"
                  className="block mt-2 font-semibold text-green-600"
                >
                  FINAL APPROVAL ✅
                </Text>
              )}
              {note.is_final_reject && (
                <Text
                  type="danger"
                  className="block mt-2 font-semibold text-red-600"
                >
                  REJECTED ❌
                </Text>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* === Input baru hanya untuk approver sesuai login user === */}
      {!finalApproved &&
        !rejected &&
        approvalNotes.length < 3 &&
        // approverMap[currentApprover] === loginUser
        approverMap[currentApprover]?.includes(loginUser) && (
          <>
            <Divider />
            <Card
              title={`Input oleh ${approverMap[currentApprover]}`}
              className="border"
            >
              <TextArea
                rows={4}
                placeholder="Tulis catatan approval di sini..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
              <Row align="left" className="mt-4 flex flex-col gap-2">
                <Checkbox
                  checked={isFinal}
                  onChange={(e) => {
                    setIsFinal(e.target.checked);
                    if (e.target.checked) setIsReject(false);
                  }}
                  disabled={isReject}
                >
                  <span className="font-medium text-gray-700">
                    Tandai sebagai FINAL APPROVAL
                  </span>
                </Checkbox>

                <Checkbox
                  checked={isReject}
                  onChange={(e) => {
                    setIsReject(e.target.checked);
                    if (e.target.checked) setIsFinal(false);
                  }}
                  disabled={isFinal}
                >
                  <span className="font-medium text-red-600">
                    Tandai sebagai REJECT ❌
                  </span>
                </Checkbox>
              </Row>

              <div className="mt-4 text-right">
                <Button
                  type="primary"
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  disabled={!newNote.trim() || isViewer}
                >
                  Simpan Catatan
                </Button>
              </div>
            </Card>
          </>
        )}

      <Modal
        title="Konfirmasi Reject"
        open={rejectModalVisible}
        onOk={handleReject}
        confirmLoading={isSubmitting}
        onCancel={() => setRejectModalVisible(false)}
        okText="Ya, Reject"
        cancelText="Batal"
        okButtonProps={{ danger: true }}
      >
        <p>Apakah Anda yakin ingin menolak (Reject) pengajuan ini?</p>
      </Modal>
    </div>
  );
};

export default TabApproval;
